import { useCallbackEvent } from '@dni-react-hooks/use-callback-event'
import { useSyncExternalStore } from 'react'

interface DialogState {
  open: boolean
}
interface AsyncDialogExtras {
  onSettle?: (confirmed: boolean) => void
}
type Store = Record<string, (DialogState & AsyncDialogExtras) | undefined>
type Listener = () => void
type ListenerMap = Record<string, Listener[] | undefined>

const store: Store = {}
const listenerMap: ListenerMap = {}

function emitChange(key: string) {
  for (const listener of listenerMap[key] || []) {
    listener()
  }
}

const setDialogState = (key: string, state: DialogState & AsyncDialogExtras) => {
  store[key] = state
  emitChange(key)
}

const defaultDialogState: DialogState = {
  open: false,
}

export const useDialog = <ExtraProps>(key: string) => {
  const getSnapshot = () => store[key]
  const getServerSnapshot = () => defaultDialogState
  const subscribe = (listener: Listener) => {
    listenerMap[key] = [...(listenerMap[key] || []), listener]
    return () => {
      listenerMap[key] = listenerMap[key]?.filter(l => l !== listener)
    }
  }

  const dialogProps = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot)

  /* extraProps 是取代而非合併：上次開窗帶入的值不會殘留到這次，呼叫端不需要自己清 */
  const openDialog = useCallbackEvent((extraProps?: ExtraProps) => {
    setDialogState(key, {
      ...extraProps,
      open: true,
    })
  })

  /* confirm 類元件常在「確認」時同時觸發 confirm 與 close 兩個回呼，包成 single-shot 只認第一次 */
  const openDialogAsync = useCallbackEvent((extraProps?: ExtraProps) =>
    new Promise<boolean>((resolve) => {
      let settled = false
      const onSettle = (confirmed: boolean) => {
        if (settled) return
        settled = true
        resolve(confirmed)
      }
      setDialogState(key, {
        ...extraProps,
        onSettle,
        open: true,
      })
    }),
  )

  const closeDialog = useCallbackEvent(() => {
    /* 關閉即視為取消，保底 resolve(false)，讓 openDialogAsync 的 promise 不會因 consumer 漏呼而永遠掛著 */
    store[key]?.onSettle?.(false)
    setDialogState(key, {
      ...dialogProps,
      open: false,
    })
  })

  return {
    openDialog,
    openDialogAsync,
    closeDialog,
    ...(dialogProps || defaultDialogState) as DialogState & ExtraProps,
  }
}
